import { useRouter } from 'next/router';
import {
  Button,
  Flex,
  Icon,
  ImgProps,
  Stack,
  useBreakpointValue,
} from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { default as NextLink } from 'next/link';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisIcon,
} from '../../config/icons';
import usePagination from '../../hooks/usePagination';

type PaginationProps = ImgProps & {
  param?: string;
  total?: number;
  perPage: number;
  currentPage?: number;
  totalPages?: number;
  onNext?: () => void;
  onPrev?: () => void;
  onGoToPage?: (page: number) => void;
};

const Pagination: React.FC<PaginationProps> = ({
  param = 'page',
  total,
  perPage,
  currentPage,
  totalPages,
  onNext,
  onPrev,
  onGoToPage,
  ...props
}) => {
  const router = useRouter();
  const [paginationSettings, setPaginationSettings] = useState<{
    maxPages: number;
    renderEndPages: boolean;
    renderStartPages: boolean;
  }>({
    maxPages: 9,
    renderEndPages: true,
    renderStartPages: true,
  });
  const maxPagesBreakpointValues = useBreakpointValue({
    base: 3,
    md: 6,
    lg: 9,
  });
  const renderStartAndEndPages = useBreakpointValue({
    base: false,
    md: true,
    lg: true,
  });
  const { pageList } = usePagination({
    ...paginationSettings,
    perPage,
    total,
    param,
    currentPage,
    totalPages,
  });

  useEffect(() => {
    setPaginationSettings({
      maxPages: maxPagesBreakpointValues,
      renderStartPages: renderStartAndEndPages,
      renderEndPages: renderStartAndEndPages,
    });
  }, [maxPagesBreakpointValues, renderStartAndEndPages]);

  return (
    <Stack marginY={8} justifyContent="center" direction="row" wrap="wrap">
      {pageList.map((page, i) => {
        if (page === 'ellipsis') {
          return (
            <Flex
              key={i}
              alignItems="flex-end"
              pb={1.5}
              px={2}
              fontSize="sm"
              display={{
                base: 'none',
                md: 'flex',
              }}
            >
              <Icon as={EllipsisIcon} />
            </Flex>
          );
        }

        if (page === 'previous' && currentPage === 1) {
          return (
            <Button
              variant="outline"
              colorScheme="brand"
              key="prev-disabled"
              isDisabled
            >
              <Icon as={ChevronLeftIcon} />
            </Button>
          );
        }

        if (page === 'next' && currentPage === totalPages) {
          return (
            <Button
              key="next-disabled"
              variant="outline"
              colorScheme="brand"
              isDisabled
            >
              <Icon as={ChevronRightIcon} />
            </Button>
          );
        }

        const isActive = page === currentPage;
        const pageUrlParam =
          page === 'previous'
            ? `${Math.max(1, currentPage - 1)}`
            : page === 'next'
            ? `${Math.min(totalPages, currentPage + 1)}`
            : `${page}`;

        if (onNext && onPrev && onGoToPage) {
          return (
            <Button
              key={`${page}-${i}`}
              as="a"
              variant="outline"
              colorScheme="brand"
              isActive={isActive}
              cursor="pointer"
              onClick={() => {
                if (typeof page === 'number') {
                  onGoToPage(page);
                } else if (page === 'previous') {
                  onPrev();
                } else {
                  onNext();
                }
              }}
            >
              {typeof page === 'number' ? (
                page
              ) : (
                <Icon
                  as={page === 'previous' ? ChevronLeftIcon : ChevronRightIcon}
                />
              )}
            </Button>
          );
        }

        return (
          <NextLink
            key={`${page}-${i}`}
            href={{
              pathname: router.pathname,
              query: {
                ...router.query,
                page: pageUrlParam,
              },
            }}
            passHref
          >
            <Button
              as="a"
              variant="outline"
              colorScheme="brand"
              isActive={isActive}
            >
              {typeof page === 'number' ? (
                page
              ) : (
                <Icon
                  as={page === 'previous' ? ChevronLeftIcon : ChevronRightIcon}
                />
              )}
            </Button>
          </NextLink>
        );
      })}
    </Stack>
  );
};

export default Pagination;
